import { escapeHtml } from '../lib/html.js';
import {
  normalizeShopDomain,
  shopifyDeviceCreateApp,
  shopifyDeviceDiscard,
  shopifyDevicePoll,
  shopifyDeviceStart,
  shopifyInstallDiscard,
  shopifyInstallStart,
  shopifyInstallStatus,
  listShopifyLojas,
  syncShopifyLoja,
  shopifyPlatformInfo,
} from '../lib/shopify.js';
import { showToast } from './toast.js';
import { isAdmin } from '../lib/auth.js';
import {
  LOJA_WIZARD_STEPS,
  LOJA_WIZARD_STEPS_REMOTE,
  SHOPIFY_DEVICE_ERRORS,
  SHOPIFY_INSTALL_ERRORS,
  SHOPIFY_PERMISSIONS,
  SHOPIFY_SYNC_PHASES,
} from '../data/loja-wizard.js';

let stepIndex = 0;
let open = false;
let manualDomain = false;
let deviceJobId = null;
let installJobId = null;
let pollTimer = null;
let pollIntervalMs = 4000;
let userCode = null;
let verificationUri = null;
let stores = [];
let selectedShop = '';
let connectedLoja = null;
let historicoParcial = false;
let startingDevice = false;
let installing = false;
let installPollStreak = 0;
let platformHint = null;
let useCliFlow = false;
let installMode = null;
/** Lojas com app custom já criado nesta sessão CLI (Summit). */
const customAppsReady = new Set();
let creatingApp = false;
/** Timestamp do último «kick» de sync quando a fila fica parada em queued. */
let importSyncKickAt = 0;

function wizardSteps() {
  return useCliFlow ? LOJA_WIZARD_STEPS : LOJA_WIZARD_STEPS_REMOTE;
}

function installStepIndex() {
  return useCliFlow ? 3 : 1;
}

function importStepIndex() {
  return useCliFlow ? 4 : 2;
}

function wizardEl() {
  return document.getElementById('loja-wizard');
}

export function isLojaWizardOpen() {
  return open;
}

function clearPoll() {
  if (pollTimer) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
}

function discardJobs() {
  if (deviceJobId) shopifyDeviceDiscard(deviceJobId);
  if (installJobId) shopifyInstallDiscard(installJobId);
}

function resetState() {
  clearPoll();
  discardJobs();
  clearPersistedInstallJob();
  stepIndex = 0;
  manualDomain = false;
  deviceJobId = null;
  installJobId = null;
  pollIntervalMs = 4000;
  userCode = null;
  verificationUri = null;
  stores = [];
  selectedShop = '';
  connectedLoja = null;
  historicoParcial = false;
  startingDevice = false;
  installing = false;
  installPollStreak = 0;
  platformHint = null;
  installMode = null;
  customAppsReady.clear();
  creatingApp = false;
}

function deviceErrorMessage(code) {
  return SHOPIFY_DEVICE_ERRORS[code] || 'Não foi possível conectar com a Shopify — tente de novo.';
}

function installErrorMessage(code, motivo) {
  if (motivo) return motivo;
  const key = String(code || '').toUpperCase();
  return SHOPIFY_INSTALL_ERRORS[key] || SHOPIFY_INSTALL_ERRORS[code] || 'Não foi possível instalar o app — tente de novo.';
}

function syncPhaseIndex(status) {
  const order = ['queued', 'products', 'orders', 'done'];
  const idx = order.indexOf(status);
  if (idx >= 0) return idx;
  if (status === 'syncing') return 1;
  if (status === 'idle') return 0;
  if (status === 'error') return order.length;
  return 0;
}

function syncProgressBar(current, total, { active = false } = {}) {
  const cur = Math.max(0, Number(current) || 0);
  const tot = Math.max(0, Number(total) || 0);
  let pct = 0;
  if (tot > 0) pct = Math.min(100, (cur / tot) * 100);
  else if (cur > 0) pct = Math.min(92, 12 + cur * 0.4);
  const pulsing = tot <= 0 && active && cur >= 0;
  const label = tot > 0
    ? `${cur.toLocaleString('pt-BR')} / ${tot.toLocaleString('pt-BR')}`
    : `${cur.toLocaleString('pt-BR')} importado(s)…`;
  return `<div class="wizard-sync-progress">
    <div class="wizard-sync-progress-meta"><span class="mono">${label}</span></div>
    <div class="wizard-sync-progress-track" role="progressbar" aria-valuenow="${Math.round(pct)}" aria-valuemin="0" aria-valuemax="100">
      <div class="wizard-sync-progress-fill${pulsing ? ' pulse' : ''}" style="width:${pct.toFixed(1)}%"></div>
    </div>
  </div>`;
}

function renderSide() {
  const steps = wizardSteps();
  const total = steps.length;
  const done = stepIndex;
  return `
    <p class="wizard-kicker">Progresso</p>
    <h2 class="wizard-brand">Conectar Shopify</h2>
    <p class="wizard-progress-label">${done}/${total} etapas</p>
    <div class="wizard-steps">
      ${steps.map((item, idx) => {
        const state = idx < stepIndex ? 'done' : idx === stepIndex ? 'active' : 'idle';
        return `<button type="button" class="wizard-step ${state}" data-wizard-step="${idx}" disabled>
          <span class="wizard-step-dot">${idx < stepIndex ? '✓' : idx + 1}</span>
          <span class="wizard-step-copy">
            <span class="wizard-step-label">${escapeHtml(item.label)}</span>
            <span class="wizard-step-hint">${escapeHtml(item.hint)}</span>
          </span>
        </button>`;
      }).join('')}
    </div>`;
}

function renderBars() {
  return wizardSteps().map((_, idx) =>
    `<button type="button" class="wizard-bar${idx <= stepIndex ? ' on' : ''}" data-wizard-step="${idx}" aria-label="Etapa ${idx + 1}"></button>`,
  ).join('');
}

function permissionField(item) {
  return `<article class="wizard-field">
    <div class="wizard-field-head">
      <div>
        <div class="wizard-field-label">${escapeHtml(item.code)}</div>
        <span class="wizard-field-action do">vai pedir</span>
      </div>
    </div>
    <p>${escapeHtml(item.description)}</p>
  </article>`;
}

function renderRemoteIntroBody() {
  return `
    <ol class="wizard-list">
      <li><b>Informe o domínio</b> admin da loja (formato minhaloja.myshopify.com).</li>
      <li><b>Autorize</b> o app Torre de Controle na janela que a Shopify abrir.</li>
    </ol>
    <div class="field">
      <label>Domínio admin da loja</label>
      <input type="text" name="shop_domain" placeholder="minhaloja.myshopify.com" autocomplete="off">
    </div>
    <div class="wizard-note">App remoto em <b>app.torredecontrole.org</b> — mesmo fluxo da App Store.</div>
    <div class="wizard-fields">
      ${SHOPIFY_PERMISSIONS.map((item) => permissionField(item)).join('')}
    </div>`;
}

function renderIntroBody() {
  const manualBlock = manualDomain
    ? `<div class="field">
      <label>Domínio Shopify</label>
      <input type="text" name="shop_domain" placeholder="minhaloja.myshopify.com" autocomplete="off">
    </div>`
    : '';
  return `
    <ol class="wizard-list">
      <li><b>Entrar com a Shopify</b> — mesma conta que você usa no admin da loja.</li>
      <li><b>Escolher a loja</b> na lista da sua conta.</li>
      <li><b>Pronto</b> — instalamos o app, webhooks e importamos produtos e vendas.</li>
    </ol>
    ${manualBlock}
    <div class="wizard-note">Sem copiar Client ID ou secret. Tudo pelo assistente.</div>
    <div class="wizard-fields">
      ${SHOPIFY_PERMISSIONS.map((item) => permissionField(item)).join('')}
    </div>`;
}

function renderWaitingBody() {
  return `
    <div class="wizard-note">Se a aba não abriu, use o botão abaixo ou o código manual.</div>
    <div class="wizard-field">
      <div class="wizard-field-head">
        <div class="wizard-field-label">Código</div>
        ${verificationUri ? `<button type="button" class="wizard-field-copy" data-wizard="reopen-device">Abrir de novo</button>` : ''}
      </div>
      <div class="wizard-field-value mono">${escapeHtml(userCode || '—')}</div>
    </div>
    <p style="margin:0;color:var(--text-muted);font-size:13px;">Aguardando aprovação na Shopify…</p>`;
}

function resolveOrgId(shopName, orgId = '') {
  let resolved = String(orgId || '').trim();
  if (!resolved && useCliFlow && platformHint?.deviceCreateAppEnabled) {
    const hit = stores.find((s) => s.shopName === shopName);
    resolved = String(hit?.orgId || '').trim();
  }
  return resolved;
}

function renderStoresBody() {
  if (!stores.length) {
    return `<div class="empty-state">Nenhuma loja encontrada nesta conta Shopify.</div>`;
  }
  const adminCreate = useCliFlow && platformHint?.deviceCreateAppEnabled;
  const intro = adminCreate
    ? `<p style="margin:0 0 8px;color:var(--text-muted);font-size:13px;"><b>1. Criar app</b> (só na org Partners) → <b>2. Instalar</b> (abre a Shopify — faça isso em um clique separado). Não pule o passo 2.</p>`
    : `<p style="margin:0 0 8px;color:var(--text-muted);font-size:13px;">Toque na loja para abrir a instalação do app <b>Torre de Controle</b> (OAuth / grant na Shopify).</p>`;
  const note = adminCreate
    ? `<div class="wizard-note">Summit: «Criar app» / «Instalar» publicam uma <b>nova versão</b> do app custom na org (escopos e URL). Depois do OAuth, use «Sincronizar» na loja. «Recriar app» só se precisar de outro Client ID.</div>`
    : `<div class="wizard-note">Um único app no Partners — o que muda é <em>em qual loja</em> ele é instalado.</div>`;
  const anyCustomReady = adminCreate && stores.some((s) => customAppsReady.has(s.shopName));
  const nextStep = anyCustomReady
    ? `<div class="wizard-note" style="border-color:var(--accent, #3b82f6);background:rgba(59,130,246,0.08);">
        <b>App custom OK — falta instalar na loja.</b> Clique o botão azul <b>Instalar</b> no card da loja
        (ou <b>Instalar agora</b> embaixo). Vamos para a <b>etapa 4</b>: a Shopify pede permissão; ao aceitar, o assistente avança sozinho.
      </div>`
    : '';
  return `
    ${nextStep}
    ${intro}
    ${note}
    <div class="wizard-chips">
      ${stores
        .map((store) => {
          const shop = store.shopName;
          const org = store.orgId || '';
          const busy = installing && selectedShop === shop;
          const creating = creatingApp && selectedShop === shop;
          const hasCustom = customAppsReady.has(shop);
          if (adminCreate) {
            return `<div class="wizard-chip wizard-chip-split">
        <div class="wizard-chip-main">
          <span class="wizard-chip-code">${escapeHtml(shop)}</span>
          ${store.domain ? `<span class="wizard-chip-desc">${escapeHtml(store.domain)}</span>` : ''}
          ${hasCustom ? `<span class="wizard-chip-badge">✓ App na org — próximo: Instalar (OAuth)</span>` : ''}
        </div>
        <div class="wizard-chip-actions">
          ${hasCustom ? `<button type="button" class="btn btn-ghost" data-store-action="sync-app" data-shop="${escapeHtml(shop)}" data-org="${escapeHtml(org)}"${creating || busy ? ' disabled' : ''}>Atualizar app</button>` : ''}
          ${hasCustom ? `<button type="button" class="btn btn-ghost" data-store-action="recreate-app" data-shop="${escapeHtml(shop)}" data-org="${escapeHtml(org)}"${creating || busy ? ' disabled' : ''}>Recriar app</button>` : ''}
          <button type="button" class="btn btn-ghost" data-store-action="create-app" data-shop="${escapeHtml(shop)}" data-org="${escapeHtml(org)}"${creating || busy ? ' disabled' : ''}>${creating ? 'Verificando…' : hasCustom ? 'Vínculo org' : 'Criar app'}</button>
          <button type="button" class="btn btn-primary" data-store-action="install" data-shop="${escapeHtml(shop)}" data-org="${escapeHtml(org)}"${busy ? ' disabled' : ''}>${busy ? 'Abrindo…' : 'Instalar'}</button>
        </div>
      </div>`;
          }
          return `<button type="button" class="wizard-chip" data-shop="${escapeHtml(shop)}" data-org="${escapeHtml(org)}"${busy ? ' disabled' : ''}>
        <span>
          <span class="wizard-chip-code">${escapeHtml(shop)}</span>
          ${store.domain ? `<span class="wizard-chip-desc">${escapeHtml(store.domain)}</span>` : ''}
        </span>
        <span class="wizard-chip-action">${busy ? 'Abrindo…' : 'Instalar app'}</span>
      </button>`;
        })
        .join('')}
    </div>`;
}

function renderInstallBody() {
  const createAppLine =
    installMode === 'device_create_app'
      ? `<div class="wizard-note">Modo <b>admin (create-app)</b> — app custom na org Shopify. Após «Instalar», a aba fecha sozinha e o assistente avança.</div>`
      : '';
  const appLine =
    installMode !== 'device_create_app' && platformHint?.clientIdHint
      ? `<div class="wizard-note">App na API: <b class="mono">${escapeHtml(platformHint.clientIdHint)}</b> — confira se é o mesmo Client ID no Dev Dashboard (não use o formulário manual da loja).</div>`
      : '';
  const legacyLine =
    platformHint?.oauthScopesMode === 'legacy_url'
      ? `<div class="wizard-note">Instalação <b>legacy</b> (scope na URL). Alinhe «Use legacy install flow» no Partner e <span class="mono">SHOPIFY_LEGACY_INSTALL=true</span> no Worker.</div>`
      : '';
  return `
    <div class="wizard-note">Loja <b class="mono">${escapeHtml(selectedShop)}</b>. Autorize o app da Torre na janela da Shopify.</div>
    ${createAppLine}
    ${appLine}
    ${legacyLine}
    <div class="wizard-note">Se aparecer «installation link can't be used»: (1) em <b>Distribution</b>, inclua esta loja ou use app público; (2) versão em <b>Release</b> com redirect <span class="mono">…/integrations/shopify/device/callback</span>; (3) clique <b>Abrir Shopify</b> aqui — nunca cole link de grant.</div>
    <div class="wizard-note" style="margin-top:12px;">
      <b>Já clicou em Instalar / Autorizar na Shopify?</b> Feche a aba da Shopify e use
      <b>«Já instalei — continuar»</b> no rodapé, ou aguarde alguns segundos — o painel confere sozinho.
    </div>
    <p style="margin:12px 0 0;color:var(--text-muted);font-size:13px;">Aguardando confirmação da instalação…</p>`;
}

function renderImportBody() {
  const loja = connectedLoja || {};
  const sync = loja.sync_status || 'queued';
  const phaseIdx = syncPhaseIndex(sync);
  const hook = loja.webhook_status || 'pendente';
  const prog = loja.sync_progress || {};
  const productsDone = Number(prog.products_done ?? loja.products_count) || 0;
  const ordersDone = Number(prog.orders_done ?? loja.orders_count) || 0;
  const productsTotal = Number(prog.products_total) || 0;
  const ordersTotal = Number(prog.orders_total) || 0;
  const ordersPartial =
    sync === 'done' && ordersTotal > 0 && ordersDone > 0 && ordersDone < ordersTotal * 0.95;
  const phases = SHOPIFY_SYNC_PHASES.map((phase, idx) => {
    let action = 'skip';
    let actionLabel = 'aguardando';
    if (sync === 'error' && idx === phaseIdx) {
      action = 'skip';
      actionLabel = 'erro';
    } else if (idx < phaseIdx || sync === 'done') {
      action = 'do';
      actionLabel = 'ok';
    } else if (idx === phaseIdx && sync !== 'done' && sync !== 'error') {
      action = 'do';
      actionLabel = 'em andamento';
    }
    let progressHtml = '';
    if (phase.key === 'products') {
      const active = sync === 'products' || (sync === 'queued' && idx === 1);
      progressHtml = syncProgressBar(productsDone, productsTotal, { active });
    }
    if (phase.key === 'orders') {
      const active = sync === 'orders';
      progressHtml = syncProgressBar(ordersDone, ordersTotal, { active });
    }
    return `<article class="wizard-field">
      <div class="wizard-field-head">
        <div class="wizard-field-label">${escapeHtml(phase.label)}</div>
        <span class="wizard-field-action ${action === 'do' ? 'do' : 'skip'}">${actionLabel}</span>
      </div>
      ${progressHtml}
    </article>`;
  }).join('');

  const hookLine = `<article class="wizard-field">
    <div class="wizard-field-head">
      <div class="wizard-field-label">Webhooks</div>
      <span class="wizard-field-action ${hook === 'ativo' ? 'do' : 'skip'}">${escapeHtml(hook)}</span>
    </div>
  </article>`;

  const err = loja.sync_error ? `<div class="wizard-note" style="border-color:var(--bad)">${escapeHtml(loja.sync_error)}</div>` : '';
  const hist = historicoParcial || ordersPartial
    ? `<div class="wizard-note">A Shopify reporta <b>${ordersTotal.toLocaleString('pt-BR')}</b> pedido(s) na loja; importamos <b>${ordersDone.toLocaleString('pt-BR')}</b>. Pedidos antigos podem ficar de fora até liberar <b>read_all_orders</b> e reinstalar o app.</div>`
    : '';

  return `${hookLine}${phases}${err}${hist}`;
}

function renderBody() {
  if (open && platformHint === null) {
    return `<div class="empty-state">Preparando conexão com a Shopify…</div>`;
  }
  if (!useCliFlow) {
    if (stepIndex === 0) return renderRemoteIntroBody();
    if (stepIndex === installStepIndex()) return renderInstallBody();
    return renderImportBody();
  }
  if (stepIndex === 0) return renderIntroBody();
  if (stepIndex === 1) return renderWaitingBody();
  if (stepIndex === 2) return renderStoresBody();
  if (stepIndex === installStepIndex()) return renderInstallBody();
  return renderImportBody();
}

function renderFoot() {
  if (open && platformHint === null) {
    return '<span></span><span class="cell-sub">Carregando…</span>';
  }
  if (stepIndex === 0 && !useCliFlow) {
    return `
      <span></span>
      <button type="button" class="btn btn-primary" data-wizard="next"${installing ? ' disabled' : ''}>
        ${installing ? 'Abrindo…' : 'Conectar loja'}
      </button>`;
  }
  if (stepIndex === 0) {
    const manualLabel = manualDomain ? 'Usar login da conta' : 'Só tenho o domínio';
    return `
      <button type="button" class="btn btn-ghost" data-wizard="manual">${escapeHtml(manualLabel)}</button>
      <button type="button" class="btn btn-primary" data-wizard="next"${startingDevice ? ' disabled' : ''}>
        ${startingDevice ? 'Abrindo…' : 'Entrar com a Shopify'}
      </button>`;
  }
  if (stepIndex === 1 && useCliFlow) {
    return `
      <button type="button" class="btn btn-ghost" data-wizard="back">Voltar</button>
      <button type="button" class="btn btn-primary" data-wizard="reopen-device">Abrir Shopify</button>`;
  }
  if (stepIndex === 2 && useCliFlow) {
    const shop = selectedShop || stores[0]?.shopName || '';
    const canInstall = shop && customAppsReady.has(shop);
    const pendingJob = installJobId || restorePersistedInstallJob();
    let rightBtn = '<span></span>';
    if (pendingJob) {
      rightBtn = `<button type="button" class="btn btn-primary" data-wizard="verify-install">Já instalei — continuar</button>`;
    } else if (canInstall) {
      rightBtn = `<span style="display:flex;flex-wrap:wrap;gap:8px;justify-content:flex-end;">
        <button type="button" class="btn btn-primary" data-wizard="verify-connection" data-shop="${escapeHtml(shop)}">Continuar — já instalei</button>
        <button type="button" class="btn btn-ghost" data-wizard="install-ready" data-shop="${escapeHtml(shop)}">Abrir Shopify de novo</button>
      </span>`;
    }
    return `<button type="button" class="btn btn-ghost" data-wizard="back">Voltar</button>${rightBtn}`;
  }
  if (stepIndex === installStepIndex()) {
    return `
      <button type="button" class="btn btn-ghost" data-wizard="back-stores">Trocar loja</button>
      <button type="button" class="btn btn-primary" data-wizard="reopen-install">Abrir Shopify</button>`;
  }
  const done = connectedLoja?.sync_status === 'done';
  const failed = connectedLoja?.sync_status === 'error';
  if (done || failed) {
    return `
      <span></span>
      <button type="button" class="btn btn-primary" data-wizard="finish">${done ? 'Ver plataformas' : 'Fechar'}</button>`;
  }
  return `<span class="cell-sub">Importando…</span><span></span>`;
}

function paint() {
  const root = wizardEl();
  if (!root) return;
  const steps = wizardSteps();
  const step = steps[stepIndex] || steps[0];
  root.querySelector('#loja-wizard-side').innerHTML = renderSide();
  let subtitle = step.subtitle;
  if (useCliFlow && stepIndex === 2 && stores.some((s) => customAppsReady.has(s.shopName))) {
    subtitle =
      'O app custom já existe na org. Agora instale-o nesta loja (OAuth) — etapa 4 do assistente.';
  }
  root.querySelector('#loja-wizard-head').innerHTML = `
    <div class="wizard-head-row">
      <span class="wizard-crumb">${escapeHtml(step.crumb)} · etapa ${stepIndex + 1} de ${steps.length}</span>
      <button type="button" class="btn btn-ghost" data-wizard="close">Cancelar</button>
    </div>
    <h3>${escapeHtml(step.title)}</h3>
    <p>${escapeHtml(subtitle)}</p>
    <div class="wizard-bars">${renderBars()}</div>`;
  root.querySelector('#loja-wizard-body').innerHTML = renderBody();
  root.querySelector('#loja-wizard-foot').innerHTML = renderFoot();
  bootstrapInstallPolling();
  if (stepIndex === importStepIndex() && (connectedLoja?.id || selectedShop) && !pollTimer) {
    beginImportPolling();
  }
}

function bootstrapInstallPolling() {
  if (!open || !useCliFlow) return;
  const job = installJobId || restorePersistedInstallJob();
  if (!job) return;
  if (!installJobId) installJobId = job;
  ensureInstallStepVisible();
  if (!pollTimer) schedulePoll(runInstallPoll, 800);
}

function consumeOAuthStorageEvent() {
  try {
    const raw = sessionStorage.getItem(OAUTH_EVENT_LS);
    if (!raw) return;
    const msg = JSON.parse(raw);
    if (!msg?.type || Date.now() - Number(msg.ts || 0) > 5 * 60 * 1000) {
      sessionStorage.removeItem(OAUTH_EVENT_LS);
      return;
    }
    sessionStorage.removeItem(OAUTH_EVENT_LS);
    onOAuthMessage({ origin: window.location.origin, data: msg });
  } catch {
    /* noop */
  }
}

async function onWizardFocusRefresh() {
  if (!open || !useCliFlow) return;
  consumeOAuthStorageEvent();
  const saved = restorePersistedInstallJob();
  if (saved) installJobId = saved;
  if (installJobId) {
    ensureInstallStepVisible();
    schedulePoll(runInstallPoll, 200);
    return;
  }
  if (stepIndex === 2 && customAppsReady.size) {
    await tryRecoverConnectedLoja();
  }
}

async function verifyShopConnection(shopName = '') {
  const shop = normalizeShopDomain(shopName || selectedShop || stores[0]?.shopName || '');
  if (!shop) {
    showToast('Escolha uma loja.');
    return;
  }
  selectedShop = shop;
  try {
    sessionStorage.removeItem(OAUTH_EVENT_LS);
  } catch {
    /* noop */
  }
  if (await tryRecoverConnectedLoja()) return;
  const saved = restorePersistedInstallJob();
  if (saved) installJobId = saved;
  if (installJobId) {
    ensureInstallStepVisible();
    try {
      const data = await shopifyInstallStatus(installJobId);
      if (data?.status === 'error') {
        if (await tryRecoverConnectedLoja()) return;
      }
      const outcome = applyInstallStatus(data);
      if (outcome === 'connected' || outcome === 'pending') return;
      if (outcome === 'error') return;
    } catch {
      /* tenta recuperar pela lista de lojas */
    }
  }
  if (await tryRecoverConnectedLoja()) return;
  showToast('Ainda não vimos a loja no painel. Use «Abrir Shopify de novo» ou aguarde 1 min e tente Continuar.');
  paint();
}

function notifyConnected() {
  window.dispatchEvent(new CustomEvent('torre:shopify-connected'));
}

function finishWizard() {
  const ok = connectedLoja?.sync_status === 'done';
  closeLojaWizard();
  if (ok) showToast('Loja conectada e importação concluída.');
  notifyConnected();
}

function schedulePoll(fn, ms = pollIntervalMs) {
  clearPoll();
  pollTimer = setTimeout(fn, ms);
}

async function runDevicePoll() {
  if (!deviceJobId || stepIndex !== 1) return;
  try {
    const data = await shopifyDevicePoll(deviceJobId);
    if (data?.status === 'pending') {
      if (data.slowDown) pollIntervalMs = Math.min(pollIntervalMs + 2000, 12000);
      schedulePoll(runDevicePoll);
      return;
    }
    if (data?.status === 'ready') {
      stores = data.stores || [];
      for (const shop of data.customAppShops || []) {
        if (shop) customAppsReady.add(shop);
      }
      if (!data.deviceCreateApp) {
        shopifyDeviceDiscard(deviceJobId);
        deviceJobId = null;
      }
      stepIndex = 2;
      clearPoll();
      paint();
      void resumeInstallIfPending();
      return;
    }
    if (data?.status === 'error') {
      showToast(deviceErrorMessage(data.error));
      resetState();
      paint();
    }
  } catch (err) {
    showToast(err?.message || 'Falha ao verificar login na Shopify.');
    resetState();
    paint();
  }
}

let lastInstallUrl = null;
const INSTALL_JOB_LS = 'torre:shopify-install-job';
const OAUTH_EVENT_LS = 'torre:shopify-oauth-event';

function persistInstallJobId(id) {
  if (!id) return;
  try {
    sessionStorage.setItem(INSTALL_JOB_LS, String(id));
  } catch {
    /* noop */
  }
}

function clearPersistedInstallJob() {
  try {
    sessionStorage.removeItem(INSTALL_JOB_LS);
  } catch {
    /* noop */
  }
}

function restorePersistedInstallJob() {
  try {
    const id = sessionStorage.getItem(INSTALL_JOB_LS);
    return id && String(id).trim() ? String(id).trim() : null;
  } catch {
    return null;
  }
}

function openInstallTab(url) {
  const tab = window.open(url, '_blank');
  if (!tab) {
    showToast('O navegador bloqueou a aba — clique em «Abrir Shopify».');
    return false;
  }
  /* Mantém window.opener para oauth-done.html avisar este assistente via postMessage. */
  return true;
}

async function createCustomAppOnly(shopName, orgId = '', { chainInstall = false, force = false } = {}) {
  if (!shopName || creatingApp || installing) return;
  selectedShop = shopName;
  creatingApp = true;
  let openInstallAfter = false;
  clearPoll();
  if (installJobId) {
    shopifyInstallDiscard(installJobId);
    installJobId = null;
    clearPersistedInstallJob();
  }
  paint();
  try {
    if (!platformHint) {
      try {
        platformHint = await shopifyPlatformInfo();
      } catch {
        platformHint = null;
      }
    }
    if (!deviceJobId) throw new Error('Sessão CLI expirada — volte e entre com a Shopify de novo.');
    const resolvedOrgId = resolveOrgId(shopName, orgId);
    if (!resolvedOrgId) {
      throw new Error('Org Shopify não veio na lista. Volte uma etapa e escolha a loja de novo.');
    }
    const data = await shopifyDeviceCreateApp({
      deviceJobId,
      shop: shopName,
      orgId: resolvedOrgId,
      action: 'create',
      force,
    });
    if (!data?.shop) throw new Error('Não foi possível criar o app na org.');
    customAppsReady.add(shopName);
    const hint = data.clientIdHint ? ` (${data.clientIdHint})` : '';
    if (data.versionSynced) {
      showToast(
        `App custom atualizado na org${hint}. Escopos: ${data.scopes || 'read_products, read_orders'}. Clique «Instalar» e aceite na Shopify.`,
      );
    } else if (data.reused || (data.alreadyExists && !force)) {
      showToast(`App «Torre de Controle» pronto na org${hint}. Agora clique em «Instalar».`);
      openInstallAfter = chainInstall;
    } else if (force) {
      showToast(`Novo app criado na org${hint}. Clique em «Instalar» quando quiser abrir a Shopify.`);
      openInstallAfter = chainInstall;
    } else {
      showToast(`App criado na org${hint}. Próximo passo: botão azul «Instalar».`);
      openInstallAfter = chainInstall;
    }
  } catch (err) {
    showToast(err?.message || 'Falha ao criar o app custom.');
  } finally {
    creatingApp = false;
    paint();
  }
  if (openInstallAfter) {
    await beginInstall(shopName, { installKind: 'custom', orgId });
  }
}

async function syncCustomAppOnly(shopName, orgId = '') {
  if (!shopName || creatingApp || installing) return;
  selectedShop = shopName;
  creatingApp = true;
  paint();
  try {
    if (!deviceJobId) throw new Error('Sessão CLI expirada — volte e entre com a Shopify de novo.');
    const resolvedOrgId = resolveOrgId(shopName, orgId);
    const data = await shopifyDeviceCreateApp({
      deviceJobId,
      shop: shopName,
      orgId: resolvedOrgId,
      action: 'sync-app',
    });
    customAppsReady.add(shopName);
    showToast(
      data?.versionSynced
        ? `Versão publicada na org. Escopos: ${data.scopes || '—'}. Clique «Instalar» na loja.`
        : 'App atualizado. Instale de novo na Shopify.',
    );
  } catch (err) {
    showToast(err?.message || 'Não foi possível atualizar o app na org.');
  } finally {
    creatingApp = false;
    paint();
  }
}

async function beginInstall(shopName, { reopen = false, orgId = '', installKind = 'auto' } = {}) {
  if (!shopName || installing || creatingApp) return;
  if (installKind === 'custom' && !customAppsReady.has(shopName)) {
    showToast('Crie o app na org antes de instalar (botão «Criar app»).');
    return;
  }
  selectedShop = shopName;
  installing = true;
  installPollStreak = 0;
  clearPoll();
  paint();
  try {
    if (installJobId) {
      shopifyInstallDiscard(installJobId);
      installJobId = null;
      clearPersistedInstallJob();
    }
    if (!platformHint) {
      try {
        platformHint = await shopifyPlatformInfo();
      } catch {
        platformHint = null;
      }
    }
    const useCustom =
      installKind === 'custom' ||
      (installKind === 'auto' && customAppsReady.has(shopName) && deviceJobId);
    let data;
    if (useCustom) {
      if (!deviceJobId) throw new Error('Sessão CLI expirada — crie o app de novo ou use domínio manual.');
      data = await shopifyDeviceCreateApp({
        deviceJobId,
        shop: shopName,
        orgId: resolveOrgId(shopName, orgId),
        action: 'install',
      });
      installMode = data?.mode || 'device_create_app';
    } else {
      data = await shopifyInstallStart(shopName);
      installMode = 'published_app';
    }
    if (!data?.jobId || !data?.installUrl) throw new Error('Não foi possível iniciar a instalação.');
    installJobId = data.jobId;
    persistInstallJobId(installJobId);
    lastInstallUrl = data.installUrl;
    stepIndex = installStepIndex();
    paint();
    openInstallTab(data.installUrl);
    schedulePoll(runInstallPoll, reopen ? 800 : 2500);
  } catch (err) {
    showToast(err?.message || 'Falha ao abrir instalação do app.');
    if (!reopen) selectedShop = '';
  } finally {
    installing = false;
    if (stepIndex === 2 && useCliFlow) paint();
  }
}

function ensureInstallStepVisible() {
  if (!useCliFlow || !installJobId) return;
  if (stepIndex < installStepIndex()) {
    stepIndex = installStepIndex();
    paint();
  }
}

function applyInstallStatus(data) {
  if (!data) return;
  if (data.shop && !selectedShop) selectedShop = data.shop;
  if (data?.status === 'pending') {
    ensureInstallStepVisible();
    return 'pending';
  }
  if (data?.status === 'error') {
    void (async () => {
      if (await tryRecoverConnectedLoja()) return;
      showToast(installErrorMessage(data.error, data.motivo));
      stepIndex = useCliFlow ? 2 : 0;
      shopifyInstallDiscard(installJobId);
      installJobId = null;
      clearPersistedInstallJob();
      paint();
    })();
    return 'error';
  }
  if (data?.status === 'connected') {
    historicoParcial = data.historico === 'parcial';
    connectedLoja = data.loja || { sync_status: 'queued', shop_domain: selectedShop };
    stepIndex = importStepIndex();
    paint();
    beginImportPolling();
    return 'connected';
  }
  return 'unknown';
}

async function tryRecoverConnectedLoja() {
  const shop = normalizeShopDomain(selectedShop);
  if (!shop) return false;
  try {
    const payload = await listShopifyLojas();
    const lojas = payload?.lojas || [];
    const hit = lojas.find((l) => normalizeShopDomain(l.shop_domain || l.domain) === shop);
    if (!hit) return false;
    connectedLoja = hit;
    historicoParcial = false;
    stepIndex = importStepIndex();
    if (installJobId) {
      shopifyInstallDiscard(installJobId);
      installJobId = null;
      clearPersistedInstallJob();
    }
    paint();
    beginImportPolling();
    showToast('Loja já conectada — importação automática em andamento.');
    return true;
  } catch {
    return false;
  }
}

async function runInstallPoll() {
  if (!installJobId) return;
  try {
    const data = await shopifyInstallStatus(installJobId);
    const outcome = applyInstallStatus(data);
    if (outcome === 'pending') {
      installPollStreak += 1;
      if (installPollStreak >= 2) {
        const recovered = await tryRecoverConnectedLoja();
        if (recovered) return;
      }
      if (installPollStreak === 12) {
        showToast('Ainda aguardando. Se já autorizou na Shopify, use «Já instalei — continuar» ou «Abrir Shopify».');
      }
      schedulePoll(runInstallPoll, 2500);
      return;
    }
    installPollStreak = 0;
  } catch (err) {
    installPollStreak += 1;
    if (err?.status === 404 || installPollStreak >= 8) {
      showToast(err?.message || 'Sessão de instalação expirou — escolha a loja de novo.');
      stepIndex = useCliFlow ? 2 : 0;
      installJobId = null;
      clearPersistedInstallJob();
      paint();
      return;
    }
    schedulePoll(runInstallPoll, 4000);
  }
}

async function resumeInstallIfPending() {
  if (!open || !useCliFlow) return;
  const saved = installJobId || restorePersistedInstallJob();
  if (!saved) return;
  installJobId = saved;
  ensureInstallStepVisible();
  schedulePoll(runInstallPoll, 400);
}

async function refreshConnectedLojaFromApi() {
  try {
    const payload = await listShopifyLojas();
    const lojas = payload?.lojas || [];
    const shop = normalizeShopDomain(selectedShop);
    const hit =
      (connectedLoja?.id &&
        lojas.find((l) => String(l.id) === String(connectedLoja.id))) ||
      (shop && lojas.find((l) => normalizeShopDomain(l.shop_domain || l.domain) === shop));
    if (hit) connectedLoja = hit;
    return hit;
  } catch {
    return null;
  }
}

function beginImportPolling() {
  if (stepIndex !== importStepIndex()) return;
  importSyncKickAt = 0;
  schedulePoll(runImportPoll, 400);
}

async function runImportPoll() {
  if (stepIndex !== importStepIndex()) return;
  if (!connectedLoja?.id && !selectedShop) return;

  try {
    if (installJobId) {
      try {
        const data = await shopifyInstallStatus(installJobId);
        if (data?.loja) connectedLoja = data.loja;
      } catch {
        /* segue pela lista de lojas */
      }
    }
    await refreshConnectedLojaFromApi();
    paint();

    const sync = connectedLoja?.sync_status;
    const lojaId = connectedLoja?.id;

    if (lojaId && (sync === 'queued' || sync === 'idle')) {
      const now = Date.now();
      if (!importSyncKickAt) importSyncKickAt = now;
      if (now - importSyncKickAt > 12_000) {
        importSyncKickAt = now;
        void syncShopifyLoja(lojaId).catch(() => {});
      }
    } else {
      importSyncKickAt = 0;
    }

    if (sync === 'done') {
      clearPoll();
      if (installJobId) {
        shopifyInstallDiscard(installJobId);
        installJobId = null;
        clearPersistedInstallJob();
      }
      showToast('Importação concluída.');
      notifyConnected();
      paint();
      return;
    }
    if (sync === 'error') {
      clearPoll();
      paint();
      return;
    }
    schedulePoll(runImportPoll, 900);
  } catch {
    schedulePoll(runImportPoll, 2500);
  }
}

function onOAuthMessage(event) {
  if (event.origin !== window.location.origin) return;
  const msg = event.data;
  if (!msg || msg.type !== 'torre:shopify-oauth') return;
  if (msg.jobId) {
    if (installJobId && msg.jobId !== installJobId) return;
    if (!installJobId) {
      installJobId = msg.jobId;
      persistInstallJobId(installJobId);
    }
  }
  if (msg.shopify === 'negado') {
    showToast(installErrorMessage('NEGADO'));
    stepIndex = useCliFlow ? 2 : 0;
    paint();
    return;
  }
  if (msg.shopify && msg.shopify !== 'ok') {
    showToast(msg.motivo || installErrorMessage(msg.shopify, msg.motivo));
    schedulePoll(runInstallPoll, 500);
    return;
  }
  if (msg.shopify === 'ok') {
    if (msg.shop) selectedShop = normalizeShopDomain(msg.shop);
    ensureInstallStepVisible();
    void (async () => {
      try {
        if (installJobId) {
          const data = await shopifyInstallStatus(installJobId);
          if (applyInstallStatus(data) === 'connected') return;
        }
      } catch {
        /* noop */
      }
      if (await tryRecoverConnectedLoja()) return;
      schedulePoll(runInstallPoll, 300);
    })();
  }
}

async function startDeviceLogin() {
  if (startingDevice) return;
  startingDevice = true;
  paint();
  try {
    const data = await shopifyDeviceStart();
    if (!data?.jobId) throw new Error('Resposta inválida da API.');
    deviceJobId = data.jobId;
    userCode = data.userCode;
    verificationUri = data.verificationUriComplete;
    pollIntervalMs = Math.max(3000, (data.interval || 5) * 1000);
    stepIndex = 1;
    paint();
    if (verificationUri) window.open(verificationUri, '_blank', 'noopener,noreferrer');
    schedulePoll(runDevicePoll);
  } catch (err) {
    showToast(err?.message || 'Não foi possível iniciar o login na Shopify.');
  } finally {
    startingDevice = false;
    if (stepIndex === 0) paint();
  }
}

async function startManualInstall() {
  const input = wizardEl()?.querySelector('[name="shop_domain"]');
  const shop = normalizeShopDomain(input?.value || '');
  if (!shop) {
    showToast('Informe o domínio admin da loja (minhaloja.myshopify.com).');
    return;
  }
  if (!shop.endsWith('.myshopify.com')) {
    showToast(`Use ${shop.split('.')[0]}.myshopify.com, não o domínio da vitrine.`);
    return;
  }
  if (!useCliFlow) {
    await beginInstall(shop);
    return;
  }
  stores = [{ shopName: shop, domain: null }];
  stepIndex = 2;
  paint();
  await beginInstall(shop);
}

export function closeLojaWizard() {
  open = false;
  resetState();
  const root = wizardEl();
  if (root) root.hidden = true;
  document.getElementById('overlay')?.classList.remove('show');
}

function resolveWizardFlow(hint) {
  return Boolean(hint?.deviceLoginEnabled);
}

export function openLojaWizard() {
  open = true;
  useCliFlow = false;
  resetState();
  const root = wizardEl();
  if (!root) return;
  root.hidden = false;
  document.getElementById('overlay')?.classList.add('show');
  paint();
  void shopifyPlatformInfo()
    .then((hint) => {
      platformHint = hint;
      useCliFlow = resolveWizardFlow(hint);
      if (open) paint();
      if (useCliFlow) void resumeInstallIfPending();
    })
    .catch(() => {
      platformHint = { deviceLoginEnabled: isAdmin(), deviceCreateAppEnabled: false };
      useCliFlow = isAdmin();
      if (open) paint();
      if (useCliFlow) void resumeInstallIfPending();
    });
}

export function bindLojaWizard() {
  const root = wizardEl();
  if (!root) return;
  window.addEventListener('message', onOAuthMessage);
  window.addEventListener('focus', () => {
    void onWizardFocusRefresh();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void onWizardFocusRefresh();
  });
  root.addEventListener('click', (e) => {
    if (e.target === root) {
      closeLojaWizard();
      return;
    }
    const closeBtn = e.target.closest('[data-wizard="close"]');
    if (closeBtn) {
      closeLojaWizard();
      return;
    }
    const finish = e.target.closest('[data-wizard="finish"]');
    if (finish) {
      finishWizard();
      return;
    }
    const storeAction = e.target.closest('[data-store-action]');
    if (storeAction) {
      const shop = storeAction.dataset.shop || '';
      const org = storeAction.dataset.org || '';
      if (storeAction.dataset.storeAction === 'create-app') {
        void createCustomAppOnly(shop, org, { force: false });
      } else if (storeAction.dataset.storeAction === 'sync-app') {
        void syncCustomAppOnly(shop, org);
      } else if (storeAction.dataset.storeAction === 'recreate-app') {
        void createCustomAppOnly(shop, org, { force: true, chainInstall: false });
      } else {
        void beginInstall(shop, {
          orgId: org,
          installKind: customAppsReady.has(shop) ? 'custom' : 'published',
        });
      }
      return;
    }
    const shopBtn = e.target.closest('[data-shop]');
    if (shopBtn && !shopBtn.closest('.wizard-chip-split')) {
      beginInstall(shopBtn.dataset.shop, { orgId: shopBtn.dataset.org || '' });
      return;
    }
    const manual = e.target.closest('[data-wizard="manual"]');
    if (manual) {
      manualDomain = !manualDomain;
      paint();
      return;
    }
    const back = e.target.closest('[data-wizard="back"]');
    if (back) {
      resetState();
      paint();
      return;
    }
    const backStores = e.target.closest('[data-wizard="back-stores"]');
    if (backStores) {
      if (installJobId) shopifyInstallDiscard(installJobId);
      installJobId = null;
      clearPoll();
      stepIndex = useCliFlow ? 2 : 0;
      paint();
      return;
    }
    const reopenDevice = e.target.closest('[data-wizard="reopen-device"]');
    if (reopenDevice && verificationUri) {
      window.open(verificationUri, '_blank', 'noopener,noreferrer');
      return;
    }
    const verifyInstall = e.target.closest('[data-wizard="verify-install"]');
    if (verifyInstall) {
      void verifyShopConnection(selectedShop);
      return;
    }
    const verifyConnection = e.target.closest('[data-wizard="verify-connection"]');
    if (verifyConnection) {
      void verifyShopConnection(verifyConnection.dataset.shop || '');
      return;
    }
    const installReady = e.target.closest('[data-wizard="install-ready"]');
    if (installReady?.dataset.shop) {
      void beginInstall(installReady.dataset.shop, { installKind: 'custom' });
      return;
    }
    const reopenInstall = e.target.closest('[data-wizard="reopen-install"]');
    if (reopenInstall && selectedShop) {
      void beginInstall(selectedShop, {
        reopen: true,
        installKind: installMode === 'device_create_app' ? 'custom' : 'published',
      });
      return;
    }
    const next = e.target.closest('[data-wizard="next"]');
    if (next) {
      if (!useCliFlow) startManualInstall();
      else if (manualDomain) startManualInstall();
      else startDeviceLogin();
    }
  });
}
